import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import EcommerceApp from "../../assets/store/app-ecommerce.png";
import EcommerceWeb from "../../assets/store/web-ecommerce.png";
import {
	Check,
	Palette,
	Typography,
	LayoutDashboard,
	Photo,
	DeviceMobile,
	World,
	ShoppingCart,
	Bell,
	Heart,
	Share,
} from "tabler-icons-react";
import { useTranslation } from "react-i18next";

const primaryColor = "#43bd98";
const primaryLight = "#e6f9f3";

export default function EcommerceDemo() {
	const [start, setStart] = useState(false);
	const { t } = useTranslation();

	useEffect(() => {
		const timeout = setTimeout(() => setStart(true), 100);
		return () => clearTimeout(timeout);
	}, []);

	const appItems = t("store.demoWeb.appMobile.list", { returnObjects: true }) || [];
	const webItems = t("store.demoWeb.webStore.list", { returnObjects: true }) || [];
	const customCards = t("store.demoWeb.customizable.cards", { returnObjects: true }) || [];

	return (
		<div style={{ width: "100%", overflowX: "hidden", fontFamily: "'Poppins', sans-serif" }}>
			
			{/* HERO - Moderno con mockups */}
			<section
				style={{
					background: "linear-gradient(135deg, #f8fdfb 0%, #e8f5f0 50%, #f0f9f6 100%)",
					padding: "8rem 2rem 6rem",
					position: "relative",
					overflow: "hidden",
				}}
			>
				{/* Decorative elements */}
				<div
					style={{
						position: "absolute",
						top: "-20%",
						right: "-10%",
						width: "500px",
						height: "500px",
						borderRadius: "50%",
						background: `radial-gradient(circle, ${primaryColor}10 0%, transparent 70%)`,
						pointerEvents: "none",
					}}
				/>
				<div
					style={{
						position: "absolute",
						bottom: "-30%",
						left: "-10%",
						width: "400px",
						height: "400px",
						borderRadius: "50%",
						background: `radial-gradient(circle, ${primaryColor}08 0%, transparent 70%)`,
						pointerEvents: "none",
					}}
				/>

				<div
					style={{
						maxWidth: "1200px",
						margin: "0 auto",
						display: "grid",
						gridTemplateColumns: "1fr 1fr",
						gap: "4rem",
						alignItems: "center",
						position: "relative",
						zIndex: 1,
					}}
					className="hero-grid"
				>
					{/* Left: Content */}
					<motion.div
						initial={{ opacity: 0, x: -40 }}
						animate={start ? { opacity: 1, x: 0 } : {}}
						transition={{ duration: 0.8 }}
						style={{ paddingTop: "2rem" }}
					>
						{/* Badge */}
						<motion.div
							initial={{ opacity: 0, y: -10 }}
							animate={start ? { opacity: 1, y: 0 } : {}}
							transition={{ duration: 0.5, delay: 0.2 }}
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: "0.5rem",
								padding: "0.5rem 1rem",
								borderRadius: "30px",
								backgroundColor: primaryLight,
								color: primaryColor,
								fontSize: "0.9rem",
								fontWeight: 600,
								marginBottom: "1.5rem",
							}}
						>
							<DeviceMobile size={18} />
							<span>+</span>
							<World size={18} />
							<span style={{ marginLeft: "0.5rem" }}>{t("store.demoWeb.hero.badge")}</span>
						</motion.div>

						<motion.h1
							initial={{ opacity: 0, y: 20 }}
							animate={start ? { opacity: 1, y: 0 } : {}}
							transition={{ duration: 0.8, delay: 0.3 }}
							style={{
								fontSize: "clamp(2rem, 5vw, 3rem)",
								fontWeight: 800,
								color: "#1a1a1a",
								lineHeight: 1.2,
								marginBottom: "1.5rem",
							}}
						>
							{t("store.demoWeb.hero.title")}
						</motion.h1>

						<motion.p
							initial={{ opacity: 0, y: 20 }}
							animate={start ? { opacity: 1, y: 0 } : {}}
							transition={{ duration: 0.8, delay: 0.4 }}
							style={{
								fontSize: "1.1rem",
								color: "#555",
								lineHeight: 1.7,
								marginBottom: "2rem",
								maxWidth: "500px",
							}}
						>
							{t("store.demoWeb.hero.subtitle")}
						</motion.p>

						{/* Quick benefits */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={start ? { opacity: 1, y: 0 } : {}}
							transition={{ duration: 0.8, delay: 0.5 }}
							style={{
								display: "flex",
								flexWrap: "wrap",
								gap: "1rem",
								marginBottom: "2rem",
							}}
						>
							{(t("store.demoWeb.hero.quickBenefits", { returnObjects: true }) || []).map((text, i) => (
								<span
									key={i}
									style={{
										display: "flex",
										alignItems: "center",
										gap: "0.5rem",
										fontSize: "0.9rem",
										color: "#444",
									}}
								>
									<Check size={16} color={primaryColor} strokeWidth={3} />
									{text}
								</span>
							))}
						</motion.div>

						<motion.a
							initial={{ opacity: 0, y: 20 }}
							animate={start ? { opacity: 1, y: 0 } : {}}
							transition={{ duration: 0.8, delay: 0.6 }}
							href="https://applaby.com/app-demo/"
							target="_blank"
							rel="noreferrer"
							style={{
								display: "inline-block",
								padding: "1rem 2.5rem",
								borderRadius: "12px",
								backgroundColor: primaryColor,
								color: "#fff",
								fontSize: "1rem",
								fontWeight: 600,
								textDecoration: "none",
								boxShadow: `0 8px 25px ${primaryColor}40`,
								transition: "all 0.3s ease",
							}}
						>
							{t("store.demoWeb.hero.button")}
						</motion.a>
					</motion.div>

					{/* Right: Mockups */}
					<motion.div
						initial={{ opacity: 0, x: 40 }}
						animate={start ? { opacity: 1, x: 0 } : {}}
						transition={{ duration: 0.8, delay: 0.4 }}
						style={{
							position: "relative",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							minHeight: "500px",
						}}
					>
						{/* Web mockup - Behind */}
						<motion.div
							initial={{ opacity: 0, scale: 0.9 }}
							animate={start ? { opacity: 1, scale: 1 } : {}}
							transition={{ duration: 0.8, delay: 0.6 }}
							style={{
								position: "absolute",
								right: "10%",
								top: "10%",
								backgroundColor: "#fff",
								borderRadius: "16px",
								padding: "12px",
								boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
								width: "320px",
								zIndex: 1,
							}}
						>
							{/* Browser bar */}
							<div
								style={{
									backgroundColor: "#f5f5f5",
									borderRadius: "8px",
									padding: "8px 12px",
									display: "flex",
									alignItems: "center",
									gap: "8px",
									marginBottom: "8px",
								}}
							>
								<div style={{ display: "flex", gap: "4px" }}>
									<span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#ff5f57" }} />
									<span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#febc2e" }} />
									<span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#28c840" }} />
								</div>
								<span style={{ flex: 1, textAlign: "center", fontSize: "0.75rem", color: "#999" }}>
									tutienda.com
								</span>
							</div>
							<img
								src={EcommerceWeb}
								alt="Web tienda"
								style={{ width: "100%", borderRadius: "8px" }}
							/>
							<div
								style={{
									position: "absolute",
									top: "-10px",
									right: "-10px",
									backgroundColor: primaryColor,
									color: "#fff",
									padding: "6px 12px",
									borderRadius: "20px",
									fontSize: "0.75rem",
									fontWeight: 600,
								}}
							>
								WEB
							</div>
						</motion.div>

						{/* App mockup - Front */}
						<motion.div
							initial={{ opacity: 0, scale: 0.9 }}
							animate={start ? { opacity: 1, scale: 1 } : {}}
							transition={{ duration: 0.8, delay: 0.8 }}
							style={{
								position: "absolute",
								left: "5%",
								bottom: "5%",
								backgroundColor: "#1a1a1a",
								borderRadius: "32px",
								padding: "8px",
								boxShadow: "0 25px 70px rgba(0,0,0,0.25)",
								width: "200px",
								zIndex: 2,
							}}
						>
							<div
								style={{
									backgroundColor: "#fff",
									borderRadius: "26px",
									overflow: "hidden",
								}}
							>
								<img
									src={EcommerceApp}
									alt="App tienda"
									style={{ width: "100%", display: "block" }}
								/>
							</div>
							<div
								style={{
									position: "absolute",
									top: "-10px",
									left: "-10px",
									backgroundColor: "#1a1a1a",
									color: "#fff",
									padding: "6px 12px",
									borderRadius: "20px",
									fontSize: "0.75rem",
									fontWeight: 600,
								}}
							>
								APP
							</div>
						</motion.div>
					</motion.div>
				</div>

				<style>{`
					@media (max-width: 900px) {
						.hero-grid {
							grid-template-columns: 1fr !important;
							text-align: center !important;
							gap: 3rem !important;
						}
						.hero-grid > div:first-child {
							padding-top: 0 !important;
						}
						.hero-grid > div:last-child {
							min-height: 400px !important;
						}
					}
				`}</style>
			</section>

			{/* SECCIÓN: App + Web = Cobertura total */}
			<section style={{ padding: "5rem 2rem", backgroundColor: "#fff" }}>
				<div style={{ maxWidth: "1000px", margin: "0 auto", textAlign: "center" }}>
					<motion.h2
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6 }}
						viewport={{ once: true }}
						style={{
							fontSize: "clamp(1.6rem, 4vw, 2.2rem)",
							fontWeight: 700,
							color: "#1a1a1a",
							marginBottom: "3rem",
						}}
					>
						{t("store.demoWeb.whyBoth.title")} <span style={{ color: primaryColor }}>{t("store.demoWeb.whyBoth.highlight")}</span>?
					</motion.h2>

					<div
						style={{
							display: "grid",
							gridTemplateColumns: "1fr 1fr",
							gap: "2rem",
						}}
						className="app-web-grid"
					>
						{/* App Card */}
						<motion.div
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6 }}
							viewport={{ once: true }}
							style={{
								backgroundColor: "#f8f8f8",
								borderRadius: "20px",
								padding: "2rem",
								textAlign: "left",
							}}
						>
							<div
								style={{
									width: "50px",
									height: "50px",
									borderRadius: "12px",
									backgroundColor: primaryLight,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									marginBottom: "1.5rem",
								}}
							>
								<DeviceMobile size={26} color={primaryColor} />
							</div>
							<h3 style={{ fontSize: "1.3rem", fontWeight: 700, marginBottom: "0.5rem", color: "#1a1a1a" }}>
								{t("store.demoWeb.appMobile.title")}
							</h3>
							<p style={{ color: "#666", fontSize: "0.95rem", marginBottom: "1.5rem", lineHeight: 1.6 }}>
								{t("store.demoWeb.appMobile.subtitle")}
							</p>
							<div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
								{appItems.slice(0, 4).map((item, i) => (
									<div key={i} style={{ display: "flex", alignItems: "center", gap: "0.6rem", color: "#444", fontSize: "0.9rem" }}>
										<Check size={16} color={primaryColor} strokeWidth={3} />
										{item}
									</div>
								))}
							</div>
						</motion.div>

						{/* Web Card */}
						<motion.div
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.1 }}
							viewport={{ once: true }}
							style={{
								backgroundColor: "#f8f8f8",
								borderRadius: "20px",
								padding: "2rem",
								textAlign: "left",
							}}
						>
							<div
								style={{
									width: "50px",
									height: "50px",
									borderRadius: "12px",
									backgroundColor: primaryLight,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									marginBottom: "1.5rem",
								}}
							>
								<World size={26} color={primaryColor} />
							</div>
							<h3 style={{ fontSize: "1.3rem", fontWeight: 700, marginBottom: "0.5rem", color: "#1a1a1a" }}>
								{t("store.demoWeb.webStore.title")}
							</h3>
							<p style={{ color: "#666", fontSize: "0.95rem", marginBottom: "1.5rem", lineHeight: 1.6 }}>
								{t("store.demoWeb.webStore.subtitle")}
							</p>
							<div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
								{webItems.slice(0, 4).map((item, i) => (
									<div key={i} style={{ display: "flex", alignItems: "center", gap: "0.6rem", color: "#444", fontSize: "0.9rem" }}>
										<Check size={16} color={primaryColor} strokeWidth={3} />
										{item}
									</div>
								))}
							</div>
						</motion.div>
					</div>

					{/* Message */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						viewport={{ once: true }}
						style={{
							marginTop: "2.5rem",
							padding: "1.5rem 2rem",
							backgroundColor: primaryLight,
							borderRadius: "16px",
							display: "inline-flex",
							alignItems: "center",
							gap: "0.75rem",
						}}
					>
						<span style={{ color: "#333", fontSize: "1rem" }}>
							{t("store.demoWeb.whyBoth.message")}
						</span>
						<span style={{ color: primaryColor, fontWeight: 700 }}>{t("store.demoWeb.whyBoth.highlight2")}</span>
					</motion.div>
				</div>

				<style>{`
					@media (max-width: 700px) {
						.app-web-grid {
							grid-template-columns: 1fr !important;
						}
					}
				`}</style>
			</section>

			{/* SECCIÓN: Funcionalidades destacadas */}
			<section style={{ padding: "5rem 2rem", backgroundColor: "#f9fafb" }}>
				<div style={{ maxWidth: "1100px", margin: "0 auto" }}>
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6 }}
						viewport={{ once: true }}
						style={{ textAlign: "center", marginBottom: "3rem" }}
					>
						<span
							style={{
								display: "inline-block",
								padding: "0.5rem 1rem",
								borderRadius: "20px",
								backgroundColor: primaryLight,
								color: primaryColor,
								fontSize: "0.85rem",
								fontWeight: 600,
								marginBottom: "1rem",
							}}
						>
							{t("store.demoWeb.customizable.badge")}
						</span>
						<h2
							style={{
								fontSize: "clamp(1.6rem, 4vw, 2.2rem)",
								fontWeight: 700,
								color: "#1a1a1a",
								marginBottom: "1rem",
							}}
						>
							{t("store.demoWeb.customizable.title")}
						</h2>
						<p style={{ color: "#666", fontSize: "1rem", maxWidth: "600px", margin: "0 auto" }}>
							{t("store.demoWeb.customizable.subtitle")}
						</p>
					</motion.div>

					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(4, 1fr)",
							gap: "1.5rem",
						}}
						className="features-grid"
					>
						{customCards.map((card, i) => (
							<motion.div
								key={i}
								initial={{ opacity: 0, y: 30 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: i * 0.1 }}
								viewport={{ once: true }}
								style={{
									backgroundColor: "#fff",
									borderRadius: "16px",
									padding: "1.5rem",
									textAlign: "center",
									boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
									border: "1px solid #f0f0f0",
								}}
							>
								<div
									style={{
										width: "56px",
										height: "56px",
										borderRadius: "14px",
										backgroundColor: primaryLight,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										margin: "0 auto 1rem",
									}}
								>
									{card.icon === "palette" && <Palette size={26} color={primaryColor} />}
									{card.icon === "typography" && <Typography size={26} color={primaryColor} />}
									{card.icon === "dashboard" && <LayoutDashboard size={26} color={primaryColor} />}
									{card.icon === "photo" && <Photo size={26} color={primaryColor} />}
								</div>
								<h3 style={{ fontSize: "1.1rem", fontWeight: 600, marginBottom: "0.5rem", color: "#1a1a1a" }}>
									{card.title}
								</h3>
								<p style={{ color: "#666", fontSize: "0.9rem", lineHeight: 1.5 }}>
									{card.desc}
								</p>
							</motion.div>
						))}
					</div>
				</div>

				<style>{`
					@media (max-width: 900px) {
						.features-grid {
							grid-template-columns: repeat(2, 1fr) !important;
						}
					}
					@media (max-width: 500px) {
						.features-grid {
							grid-template-columns: 1fr !important;
						}
					}
				`}</style>
			</section>

			{/* CTA Final */}
			<section
				style={{
					padding: "5rem 2rem",
					background: `linear-gradient(135deg, ${primaryColor} 0%, #2d9a7a 100%)`,
					textAlign: "center",
				}}
			>
				<div style={{ maxWidth: "700px", margin: "0 auto" }}>
					<motion.h2
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6 }}
						viewport={{ once: true }}
						style={{
							fontSize: "clamp(1.8rem, 4vw, 2.4rem)",
							fontWeight: 700,
							color: "#fff",
							marginBottom: "1rem",
						}}
					>
						{t("store.demoWeb.cta.title")}
					</motion.h2>

					<motion.p
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.1 }}
						viewport={{ once: true }}
						style={{
							color: "rgba(255,255,255,0.9)",
							fontSize: "1.1rem",
							marginBottom: "2rem",
							lineHeight: 1.6,
						}}
					>
						{t("store.demoWeb.cta.text")}
					</motion.p>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						viewport={{ once: true }}
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: "1rem",
						}}
					>
						<a
							href="https://applaby.com/app-demo/"
							target="_blank"
							rel="noreferrer"
							style={{
								display: "inline-block",
								padding: "1rem 2.5rem",
								borderRadius: "12px",
								backgroundColor: "#fff",
								color: primaryColor,
								fontSize: "1rem",
								fontWeight: 600,
								textDecoration: "none",
								boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
								transition: "all 0.3s ease",
							}}
						>
							{t("store.demoWeb.cta.button")}
						</a>
						<span style={{ color: "rgba(255,255,255,0.8)", fontSize: "0.9rem" }}>
							{t("store.demoWeb.cta.trial")}
						</span>
					</motion.div>
				</div>
			</section>
		</div>
	);
}
